import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  UtensilsCrossed,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
} from 'lucide-react';

import { getAllOrders, updateOrderStatus as updateOrderStatusAPI, verifyUserPhone as verifyUserPhoneAPI } from '../services/api';
import { Order } from '../types';
import MenuManagement from '../components/admin/MenuManagement';
import TableManagement from '../components/admin/TableManagement';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { notificationService } from '../services/notifications';
import './Admin.css';

const Admin: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial tab from URL or default to 'dashboard'
  const initialTab = (searchParams.get('tab') as 'dashboard' | 'orders' | 'menu' | 'tables' | 'users') || 'dashboard';
  const [activeTab, setActiveTab] = useState<'dashboard' | 'orders' | 'menu' | 'tables' | 'users'>(initialTab);
  
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alertsEnabled, setAlertsEnabled] = useState(notificationService.enabled);

  // Sync alert toggle state with the notification service
  useEffect(() => {
    const unsubscribe = notificationService.onStateChange(setAlertsEnabled);
    return unsubscribe;
  }, []);

  // Update URL when tab changes
  const handleTabChange = (tab: 'dashboard' | 'orders' | 'menu' | 'tables' | 'users') => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  useEffect(() => {
    // Only fetch orders when auth is ready and user is logged in
    if (!authLoading && user) {
      fetchOrders();
    } else if (!authLoading && !user) {
      setLoading(false);
      setError('Please log in to access the admin panel.');
    }
  }, [authLoading, user]);

  // Polling mechanism to fetch new orders and trigger notifications
  // We use polling (REST API) instead of onSnapshot because Firebase Security
  // rules might silently block direct client access to the 'orders' collection over WebSockets.
  useEffect(() => {
    if (!user) return;

    let isInitialLoad = true;
    const knownOrderIds = new Set<string>();

    const pollOrders = async () => {
      try {
        const response = await getAllOrders();
        const latestOrders = (response.data.data || []) as Order[];
        
        // Silently update the UI with fresh orders
        setOrders(latestOrders);

        if (isInitialLoad) {
          // On first load, just record what's already there so we don't spam notifications
          latestOrders.forEach(o => knownOrderIds.add(o.id));
          isInitialLoad = false;
        } else {
          // On subsequent polls, check if we have any NEW pending orders
          latestOrders.forEach(o => {
            if (o.status === 'pending' && !knownOrderIds.has(o.id)) {
              console.log('New order detected via polling:', o.id);
              // Trigger OS Notification & Sound
              notificationService.notifyNewOrder(o.id);
              // Trigger Guaranteed In-App UI Toast
              showToast(`🔔 New Order Received! #${o.id.slice(0, 8)}`, 'success');
            }
            // Always add them to known after we've checked so we don't alert again
            knownOrderIds.add(o.id);
          });
        }
      } catch (error) {
        console.error('Background order refresh (polling) failed:', error);
      }
    };

    // Start polling every 10 seconds
    const intervalId = setInterval(pollOrders, 10000);

    return () => clearInterval(intervalId);
  }, [user]);

  const fetchOrders = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      const response = await getAllOrders();
      setOrders(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to fetch orders:', error);
      if (error.response?.status === 401) {
        setError('Session expired. Please refresh the page and log in again.');
      } else {
        setError('Failed to load orders. Please try again.');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Calculate statistics
  const stats = {
    totalOrders: orders.length,
    pendingOrders: orders.filter(o => o.status === 'pending' || o.status === 'confirmed').length,
    completedOrders: orders.filter(o => o.status === 'delivered').length,
  };

  const handleToggleAlerts = async () => {
    if (alertsEnabled) {
      notificationService.disable();
      showToast('Alerts disabled', 'success');
    } else {
      await notificationService.enable();
      showToast('Alerts enabled! You will hear sounds & see notifications for new orders.', 'success');
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        {/* Sidebar */}
        <aside className="admin-sidebar glass-card">
          <div className="admin-logo">
            <UtensilsCrossed size={32} />
            <h2>Sone Zone Cafe Admin</h2>
          </div>

          <div style={{ padding: '0 1.5rem', marginBottom: '1rem' }}>
            <button 
              className={`btn ${alertsEnabled ? 'btn-ghost' : 'btn-primary'}`}
              onClick={handleToggleAlerts}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.75rem', fontSize: '0.9rem', border: alertsEnabled ? '1px solid var(--error)' : undefined, color: alertsEnabled ? 'var(--error)' : undefined }}
            >
              {alertsEnabled ? '🔕 Disable Alerts' : '🔔 Enable Alerts'}
            </button>
          </div>

          <nav className="admin-nav">
            <button
              className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => handleTabChange('dashboard')}
            >
              <LayoutDashboard size={20} />
              Dashboard
            </button>
            <button
              className={`nav-item ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => handleTabChange('orders')}
            >
              <Package size={20} />
              Orders
              {stats.pendingOrders > 0 && (
                <span className="badge badge-primary">{stats.pendingOrders}</span>
              )}
            </button>
            <button
              className={`nav-item ${activeTab === 'menu' ? 'active' : ''}`}
              onClick={() => handleTabChange('menu')}
            >
              <UtensilsCrossed size={20} />
              Menu Items
            </button>
            <button
              className={`nav-item ${activeTab === 'tables' ? 'active' : ''}`}
              onClick={() => handleTabChange('tables')}
            >
              🍽️ Tables
            </button>
            <button
              className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => handleTabChange('users')}
            >
              <Users size={20} />
              Users
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="admin-main">
          {authLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          ) : error ? (
            <div className="glass-card" style={{ padding: '2rem', textAlign: 'center', marginBottom: '2rem', background: 'rgba(255, 68, 68, 0.1)', border: '1px solid rgba(255, 68, 68, 0.3)' }}>
              <p style={{ color: 'var(--error)', marginBottom: '1rem' }}>{error}</p>
              {user && (
                <button className="btn btn-primary" onClick={() => fetchOrders(true)}>
                  Try Again
                </button>
              )}
            </div>
          ) : (
            <>
              {activeTab === 'dashboard' && (
                <DashboardTab stats={stats} orders={orders} loading={loading} />
              )}
              {activeTab === 'orders' && (
                <OrdersTab orders={orders} loading={loading} onRefresh={fetchOrders} />
              )}
              {activeTab === 'menu' && <MenuTab />}
              {activeTab === 'tables' && <TableManagement />}
              {activeTab === 'users' && <UsersTab />}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

// Dashboard Tab Component
const DashboardTab: React.FC<{ stats: any; orders: Order[]; loading: boolean }> = ({
  stats,
  orders,
  loading,
}) => {
  const recentOrders = orders.slice(0, 5);

  return (
    <div className="dashboard-tab">
      <motion.div
        className="admin-header"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <h1>Dashboard</h1>
        <p>Overview of your restaurant</p>
      </motion.div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <motion.div
          className="stat-card glass-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="stat-icon" style={{ background: 'var(--gradient-primary)' }}>
            <Package size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Total Orders</div>
            <div className="stat-value">{stats.totalOrders}</div>
          </div>
        </motion.div>

        <motion.div
          className="stat-card glass-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="stat-icon" style={{ background: 'var(--gradient-accent)' }}>
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Pending Orders</div>
            <div className="stat-value">{stats.pendingOrders}</div>
          </div>
        </motion.div>

        <motion.div
          className="stat-card glass-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="stat-icon" style={{ background: 'var(--gradient-secondary)' }}>
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-label">Completed</div>
            <div className="stat-value">{stats.completedOrders}</div>
          </div>
        </motion.div>


      </div>

      {/* Recent Orders */}
      <motion.div
        className="recent-orders glass-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <h2>Recent Orders</h2>
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
          </div>
        ) : recentOrders.length === 0 ? (
          <div className="empty-state">
            <Package size={48} />
            <p>No orders yet</p>
          </div>
        ) : (
          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order) => (
                  <tr key={order.id}>
                    <td>#{order.id.slice(0, 8)}</td>
                    <td>{order.userId.slice(0, 8)}...</td>
                    <td>{order.items.length} items</td>
                    <td>₹{order.totalAmount.toFixed(2)}</td>
                    <td>
                      <span className={`status-badge status-${order.status}`}>
                        {order.status}
                      </span>
                    </td>
                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// Orders Tab Component
const OrdersTab: React.FC<{ orders: Order[]; loading: boolean; onRefresh: (showLoading?: boolean) => void }> = ({
  orders,
  loading,
  onRefresh,
}) => {
  const { showToast } = useToast();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const filteredOrders = filterStatus === 'all' 
    ? orders 
    : orders.filter(o => o.status === filterStatus);

  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      await updateOrderStatusAPI(orderId, newStatus);
      // Refresh orders silently without triggering a global loading spinner (keeps scroll position intact)
      onRefresh(false);
      showToast('Order status updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update order status:', error);
      showToast('Failed to update order status. Please try again.', 'error');
    }
  };

  const verifyPhone = async (orderId: string) => {
    try {
      await verifyUserPhoneAPI(orderId);
      onRefresh(false);
      showToast('Phone number verified successfully', 'success');
      // Update selected order if it's currently open
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, userPhoneVerified: true });
      }
    } catch (error) {
      console.error('Failed to verify phone:', error);
      showToast('Failed to verify phone. Please try again.', 'error');
    }
  };

  return (
    <motion.div 
      className="orders-tab"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="admin-header">
        <h1>Orders Management</h1>
        <div className="header-actions">
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Orders</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="preparing">Preparing</option>
            <option value="ready">Ready</option>
            <option value="out_for_delivery">Out for Delivery</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button className="btn btn-primary" onClick={() => onRefresh(true)}>
            <TrendingUp size={18} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="empty-state glass-card">
          <Package size={64} />
          <h3>No orders found</h3>
          <p>No orders match the selected filter</p>
        </div>
      ) : (
        <div className="orders-list">
          {filteredOrders.map((order) => (
            <div key={order.id} className={`order-item glass-card order-status-${order.status}`}>
              <div className="order-item-header">
                <div>
                  <div className="order-id">
                    Order #{order.id.slice(0, 8)}
                    {order.orderType === 'dine-in' && order.tableNumber && (
                      <span style={{
                        marginLeft: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        background: 'var(--gradient-accent)',
                        color: 'white',
                        borderRadius: '0.5rem',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        🍽️ Table {order.tableNumber}
                      </span>
                    )}
                    {!order.userPhoneVerified && (
                      <span style={{
                        marginLeft: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        background: '#ff9800',
                        color: 'white',
                        borderRadius: '0.5rem',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        ⚠️ Unverified Phone
                      </span>
                    )}
                  </div>
                  <div className="order-date">
                    {new Date(order.createdAt).toLocaleString()}
                  </div>
                </div>
                <span className={`status-badge status-${order.status}`}>
                  {order.status.replace('_', ' ')}
                </span>
              </div>

              <div className="order-item-body">
                <div className="order-items">
                  {order.items.map((item) => (
                    <div key={item.itemId} className="order-item-detail">
                      <img src={item.image} alt={item.name} />
                      <span>{item.name} x{item.quantity}</span>
                    </div>
                  ))}
                </div>

                <div className="order-total">₹{order.totalAmount.toFixed(2)}</div>
              </div>

              <div className="order-item-actions">
                <select
                  className="status-select"
                  value={order.status}
                  onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  {/* Uncomment these when needed:
                  <option value="preparing">Preparing</option>
                  <option value="ready">Ready</option>
                  <option value="out_for_delivery">Out for Delivery</option>
                  */}
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSelectedOrder(order)}
                >
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Order Details Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedOrder(null)}
          >
            <motion.div
              className="order-details-modal glass-card"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>Order Details</h2>
                <button className="close-btn" onClick={() => setSelectedOrder(null)}>
                  ✕
                </button>
              </div>

              <div className="modal-content">
                <div className="detail-row">
                  <span>Order ID</span>
                  <span>#{selectedOrder.id}</span>
                </div>
                <div className="detail-row">
                  <span>Status</span>
                  <span className={`status-badge status-${selectedOrder.status}`}>
                    {selectedOrder.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="detail-row">
                  <span>Order Date</span>
                  <span>{new Date(selectedOrder.createdAt).toLocaleString()}</span>
                </div>

                <div className="divider"></div>

                <h3>Customer Profile</h3>
                <div className="address-details-section" style={{ background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                  <p><strong>Name:</strong> {selectedOrder.userName || 'Unknown'}</p>
                  <p><strong>Email:</strong> {selectedOrder.userEmail || 'Unknown'}</p>
                  <p><strong>Profile Phone:</strong> {selectedOrder.userPhone || 'None'}</p>
                  {selectedOrder.deliveryAddress?.phone && (
                    <p><strong>Order Phone:</strong> {selectedOrder.deliveryAddress.phone}</p>
                  )}
                  <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <strong>Status:</strong>
                    {selectedOrder.userPhoneVerified ? (
                      <span style={{ color: 'var(--success)', fontWeight: 'bold' }}>✅ Verified</span>
                    ) : (
                      <span style={{ color: '#ff9800', fontWeight: 'bold' }}>⚠️ Not Verified</span>
                    )}
                  </p>
                </div>

                {!selectedOrder.userPhoneVerified && (
                  <div className="detail-row" style={{ background: '#fff3e0', padding: '1rem', borderRadius: '0.5rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e65100' }}>
                      ⚠️ <strong>New Customer / Phone Not Verified</strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#e65100' }}>Call the customer to confirm the order before accepting.</p>
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => verifyPhone(selectedOrder.id)}
                      style={{ marginTop: '0.5rem', alignSelf: 'flex-start' }}
                    >
                      Mark Phone Verified
                    </button>
                  </div>
                )}

                <div className="divider"></div>

                {selectedOrder.orderType === 'dine-in' ? (
                  <>
                    <h3>🍽️ Dine-In Order</h3>
                    <div className="address-details-section" style={{
                      padding: '1.5rem',
                      background: 'var(--gradient-accent)',
                      borderRadius: '1rem',
                      color: 'white',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                        Table {selectedOrder.tableNumber}
                      </div>
                      <div style={{ opacity: 0.9 }}>
                        Dine-in order for table service
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <h3>Delivery Address</h3>
                    <div className="address-details-section">
                      {selectedOrder.deliveryAddress?.label && (
                        <div className="address-label-badge">{selectedOrder.deliveryAddress.label}</div>
                      )}
                      {selectedOrder.deliveryAddress?.recipientName && (
                        <p><strong>{selectedOrder.deliveryAddress.recipientName}</strong></p>
                      )}
                      {selectedOrder.deliveryAddress?.phone && (
                        <p>📱 {selectedOrder.deliveryAddress.phone}</p>
                      )}
                      {selectedOrder.deliveryAddress && (
                        <>
                          <p>{selectedOrder.deliveryAddress.street}</p>
                          <p>{selectedOrder.deliveryAddress.city}, {selectedOrder.deliveryAddress.state} - {selectedOrder.deliveryAddress.pincode}</p>
                        </>
                      )}
                    </div>
                  </>
                )}

                <div className="divider"></div>

                <h3>Items</h3>
                <div className="order-items-list">
                  {selectedOrder.items.map((item) => (
                    <div key={item.itemId} className="order-item-detail-modal">
                      <img src={item.image} alt={item.name} />
                      <div className="item-info">
                        <div className="item-name">{item.name}</div>
                        <div className="item-qty">Qty: {item.quantity}</div>
                      </div>
                      <div className="item-price">₹{(item.price * item.quantity).toFixed(2)}</div>
                    </div>
                  ))}
                </div>

                <div className="divider"></div>

                <div className="detail-row total">
                  <span>Total Amount</span>
                  <span>₹{selectedOrder.totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Menu Tab Component
const MenuTab: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <MenuManagement />
    </motion.div>
  );
};

// Users Tab Component (Placeholder)
const UsersTab: React.FC = () => {
  return (
    <motion.div 
      className="users-tab"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="admin-header">
        <h1>Users Management</h1>
      </div>
      <div className="glass-card" style={{ padding: '3rem', textAlign: 'center' }}>
        <Users size={64} style={{ color: 'var(--text-muted)', marginBottom: '1rem' }} />
        <h3>Users Management</h3>
        <p style={{ color: 'var(--text-secondary)' }}>
          View and manage your customers here
        </p>
      </div>
    </motion.div>
  );
};

export default Admin;
